
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { LogOut, Bell } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { Timeline } from "@/components/Timeline";
import { useTranslation } from "react-i18next";
import { FlightDetails, Trip, TripElement } from "@/types/trip";
import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { UpcomingFlightsDialog } from "@/components/UpcomingFlightsDialog";
import { useIsMobile } from "@/hooks/use-mobile";
import DesktopLayout from "@/components/layout/DesktopLayout";
import Hey, { Trip as HeyTrip } from "@/lib/hey";
import { FlutterUtils, useFlutterBridge } from "@/hooks/useFlutterBridge";
import { config } from '../lib/hey/config';
import { enUS, fr, es, it, de, ru, sv, pt, zhTW, arSA } from 'date-fns/locale';
import { trackHeaderAction } from "@/utils/analytics";
import { useUserCurrency } from "@/hooks/useUserCurrency";
import { identifyRealDestination } from "@/utils/tripUtils";
import { getFlightStatus } from "@/utils/flightStatus";
import { HeyUtils } from "@/lib/hey/core";
import { getImageForTripId } from "@/hooks/trip-card/useBasicTripCardData";

const localeMap = {
  en: enUS,
  fr: fr,
  es: es,
  it: it,
  de: de,
  ru: ru,
  sv: sv,
  pt: pt,
  zh: zhTW,
  ar: arSA
};

const Index = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, i18n } = useTranslation();
  const [firstName, setFirstName] = useState<string | null>(null);
  const [showUpcomingFlights, setShowUpcomingFlights] = useState(false);
  const [notificationCount, setNotificationCount] = useState<number>(0);
  const isMobile = useIsMobile();
  const isFlutterWebapp = FlutterUtils.isFlutterWebapp()
  const { currency } = useUserCurrency();

  function getTimezoneOffsetHoursStr(timezone: string) {
    const date = new Date();
    const utcDate = new Date(date.toLocaleString("en-US", { timeZone: "UTC" }));
    const localDate = new Date(date.toLocaleString("en-US", { timeZone: timezone }));
    const offset = (localDate.getTime() - utcDate.getTime()) / 1000 / 60;
    let offsetRounded = Math.floor(offset * 10 / 60) / 10;
    if (offsetRounded > 0) {
      return `UTC+${offsetRounded}`;
    } else if (offsetRounded < 0) {
      return `UTC${offsetRounded}`;
    } else {
      return `UTC`;
    }
  }

  function buildDateWithTimezone(time, timezoneName) : Date {
    if (!timezoneName) {
      console.error("Time is not provided");
      return new Date(time);
    }
    
    let offsetTimezone = getTimezoneOffsetHoursStr(timezoneName);
    let baseDate = new Date(time+"Z");
    let newDateStr = baseDate.toUTCString().replace("UTC", offsetTimezone)
    let dateWithZone = new Date(newDateStr);

    return dateWithZone;
  }

  async function importAdtdTripIfNeeded() {
    try {
      if (!Hey.HeyUtils.getInstance().isUserAuthenticated()) {
        return;
      }
      let atdtData = localStorage.getItem("atdtData");
      if (atdtData) {
        atdtData = JSON.parse(atdtData);
        const importedTrip = await Hey.HeyUtils.getInstance().importAdtdTrip(atdtData);
        localStorage.removeItem("atdtData");
        if (!importedTrip) {
          toast({
            variant: "destructive",
            title: t("index.importAdtdTripError", "Error importing trip"),
            description: t("index.errorFetchingTrips", "Make sure you are connected to an account with corresponding name and email.")
          });
          return;
        }
        toast({
          title: t("index.importAdtdTripSuccess", "Trip imported successfully"),
          description: t("index.checkYourTrips", "Check your trips in the app")
        });

        console.debug("Trip imported successfully:", importedTrip);
      }
    } catch (error) {
      console.error('Error retrieving atdtData from localStorage:', error);
    }
  }


  function buildTripFromHeyTrip(heyTrip: HeyTrip): Trip {
    const elements: TripElement[] = [];
    
    let tripUid = ""
    heyTrip.products.forEach(product => {
      if (product.air) {
        const cabinClass = product.air.bookingClass?.cabin?.category || undefined;
        
        let flightDetails : FlightDetails = {
          departure: {
            code: product.air.departure.iataCode,
            city: product.air.departureAirportLocation.airportAddress?.cityName,
            time: product.air.departure.localDateTime,
            timezone: `${getTimezoneOffsetHoursStr(product.air.departure.timeZone)}`,
            date: buildDateWithTimezone(product.air.departure.localDateTime, product.air.departure.timeZone),
            terminal: product.air.departure.terminal,
            gate: product.air.departure.gate,
            airportName: product.air.departureAirportLocation.name,
            cityName: product.air.departureAirportLocation.airportAddress?.cityName
          },
          arrival: {
            code: product.air.arrival.iataCode,
            city: product.air.arrivalAirportLocation.airportAddress?.cityName,
            time: product.air.arrival.localDateTime,
            timezone: getTimezoneOffsetHoursStr(product.air.arrival.timeZone),
            date: buildDateWithTimezone(product.air.arrival.localDateTime, product.air.arrival.timeZone),
            terminal: product.air.arrival.terminal,
            gate: product.air.arrival.gate,
            airportName: product.air.arrivalAirportLocation.name,
            cityName: product.air.arrivalAirportLocation.airportAddress?.cityName
          },
          airline: product.air.marketing.carrier.name,
          flightNumber: `${product.air.marketing.flightDesignator.carrierCode}${product.air.marketing.flightDesignator.flightNumber}`,
          bookingReference: product.air.airlineRecloc,
          cabinClass: cabinClass,
          seat: product.air.seat?.number,
          confirmationNumbers: product.air.confirmationNumbers || [],
          operating: {
            airline: product.air.operating?.carrier.name,
            airlineCode: product.air.operating?.flightDesignator.carrierCode,
            flightNumber: product.air.operating?.flightDesignator.flightNumber
          },
          flightStatus: null,
        }

        tripUid += flightDetails.flightNumber+'_'+flightDetails.departure.date.getTime()+"_"
        
        flightDetails.computed = {
          departureTerminal: flightDetails.departure.terminal,
          departureGate: flightDetails.departure.gate,
          arrivalTerminal: flightDetails.arrival.terminal,
          arrivalGate: flightDetails.arrival.gate,
          departureCheckInDesk: 'N/A',
          arrivalBaggageClaim: 'N/A',
        }

        // // Debug
        // flightDetails.computed = {
        //   departureTerminal: "1",
        //   departureGate: "12",
        //   arrivalTerminal:  "2",
        //   arrivalGate: "22",
        //   departureCheckInDesk: 'N/A',
        //   arrivalBaggageClaim: 'N/A',
        // }
        // flightDetails.flightStatus = {
        //   latestStatus: {
        //     departureTimezone: flightDetails.departure.timezone,
        //     arrivalTimezone: flightDetails.arrival.timezone,
        //     estimatedDepartureLocalDate: "2023-10-01T12:00:00Z",
        //     estimatedArrivalLocalDate: "2023-10-01T14:00:00Z",
        //     departureTerminal: flightDetails.departure.terminal,
        //     departureGate: flightDetails.departure.gate,
        //     arrivalTerminal: flightDetails.arrival.terminal,
        //     arrivalGate: flightDetails.arrival.gate,
        //     departureCheckInDesk: 'N/A',
        //   },
        //   id: "string",
        //   departurePlace: "string",
        //   arrivalPlace: "string",
        //   scheduledDepartureLocalDate: "string",
        //   departureTimezone: "string",
        //   scheduledArrivalLocalDate: "string",
        //   arrivalTimezone: "string",
        // }

        elements.push({
          type: 'flight',
          startDate: product.air.departure.localDateTime,
          endDate: product.air.arrival.localDateTime,
          details: flightDetails
        });
      }
      if (product.hotel) {
        elements.push({
          type: 'hotel',
          startDate: product.hotel.checkInDate,
          endDate: product.hotel.checkOutDate,
          details: {
            name: product.hotel.name,
            address: product.hotel.address.line || 
              `${product.hotel.address.cityName || ''}, ${product.hotel.address.countryCode || ''}`.trim(),
            roomType: product.hotel.room?.roomType,
            checkIn: product.hotel.checkInDate,
            checkOut: product.hotel.checkOutDate,
            city: product.hotel.address.cityName
          }
        });
        tripUid += product.hotel.name+'_'+product.hotel.checkInDate+'_'
      }
      if (product.car) {
        elements.push({
          type: 'car',
          startDate: product.car.pickupTime,
          endDate: product.car.dropoffTime,
          details: {
            company: product.car.providerName,
            model: product.car.carModel,
            pickupLocation: product.car.pickupLocation.name,
            dropoffLocation: product.car.dropoffLocation.name,
            pickupTime: product.car.pickupTime,
            dropoffTime: product.car.dropoffTime
          }
        });
      }
    });

    elements.sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());
    
    let startDate = heyTrip.start?.localDateTime;
    let endDate = heyTrip.end?.localDateTime;
    if ((!startDate || !endDate) && elements.length > 0) {
      startDate = elements[0].startDate;
      endDate = elements[elements.length - 1].endDate;
    }
    
    startDate = startDate || new Date().toISOString();
    endDate = endDate || new Date().toISOString();
    
    const currentLocale = i18n.language;
    const dateLocale = localeMap[currentLocale as keyof typeof localeMap] || enUS;
    let parsedStartDate = parseISO(startDate || "1970");
    let parsedEndDate = parseISO(endDate || "1970");
    const formattedStartDate = format(parsedStartDate, 'MMM d, yyyy', { locale: dateLocale });
    const formattedEndDate = format(parsedEndDate, 'MMM d, yyyy', { locale: dateLocale });
    
    let destination = heyTrip.end?.name || "";
    
    if (!destination) {
      const hotelElement = elements.find(el => el.type === 'hotel');
      if (hotelElement) {
        destination = (hotelElement.details as any).city || "";
      }
    }
    
    if (!destination) {
      const flightElement = elements.find(el => el.type === 'flight');
      if (flightElement) {
        destination = (flightElement.details as any).arrival.code || "";
      }
    }

    let remainingDays = 0;
    if (parsedStartDate > new Date()) {
      remainingDays = Math.floor((parsedStartDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    } else if (parsedEndDate < new Date()) {
      remainingDays = Math.floor((parsedEndDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
    }
    
    destination = destination || "Unknown Destination";

    console.debug(`🔍 Trip Origin: ${heyTrip.origin}, Reference: ${heyTrip.reference}`);

    // hash tripUid
    if (tripUid.length > 0) {
      tripUid = tripUid.split("_").map((part) => part.substring(0, 3)).join("_");
    }
  
    let appTrip : Trip = {
      id: heyTrip.reference || Math.random().toString(36).replace(/[^a-z]+/g, '').toUpperCase(),
      destination: destination,
      dates: `${formattedStartDate} - ${formattedEndDate}`,
      image: "/lovable-uploads/656ca7e1-4c19-4848-9e88-c32e3eedb77e.png",
      elements: elements,
      isPast: parsedEndDate.getTime() < new Date().getTime() + 1000 * 60 * 60 * 24, // after 24h, trip is past (not visible in offline mode)
      startDateTimestamp: parsedStartDate.getTime(),
      remainingDays: remainingDays,
      title: heyTrip.origin,
      numberOfTravelers: heyTrip.numberOfPassengers,
      originGeoCode: heyTrip.start?.geoCode,
      destinationGeoCode: heyTrip.end?.geoCode,
      origin: heyTrip.origin,
      bookingReference: heyTrip.origin === "PNR_IMPORT_MINDSAY" ? heyTrip.reference : undefined,
      tripUid: hashCode(tripUid).toString(),
    };
    return appTrip;
  }

  function hashCode(str) {
    var hash = 0,
      i, chr;
    if (str.length === 0) return hash;
    for (i = 0; i < str.length; i++) {
      chr = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + chr;
      hash |= 0; // Convert to 32bit integer
    }
    return Math.abs(hash);
  }

  function buildFlightIdForHeyApi(flightDetails: FlightDetails) {
    const carrierCode = flightDetails.flightNumber.match(/^[A-Z]+/)?.[0] || flightDetails.airline || 'XX';
    const numericFlightNumber = flightDetails.flightNumber.replace(/^[A-Z]+/, '');

    let timezoneOffset = parseFloat(flightDetails.departure.timezone.replace("UTC", "") || "0");
    let timezoneOffsetHours = Math.floor(timezoneOffset);
    let timezoneOffsetMinutes = Math.round((timezoneOffset - timezoneOffsetHours) * 60);
    let zeroPaddedOffset = String(Math.abs(timezoneOffsetHours)).padStart(2, '0') + ':' + String(Math.abs(timezoneOffsetMinutes)).padStart(2, '0');
    let sign = timezoneOffset >= 0 ? "+" : "-";
    let utcDate = new Date(`${flightDetails.departure.time}.000${sign}${zeroPaddedOffset}`)
    let flightDate = utcDate.toISOString().split("T")[0];

    const flightId = `${carrierCode}${numericFlightNumber}_${flightDetails.departure.code}_${flightDate}`;
    return flightId;
  }

  async function retrieveFlightStatuses(retrieved_trips: Trip[]) {
    let mapFlightIdToFlightDetails : { [key: string]: FlightDetails[] } = {};
    for (let trip of retrieved_trips) {
      for (let elem of trip.elements) {
        if (elem.type === "flight") {
          let flightDetails = elem.details as FlightDetails;
          let flightId = buildFlightIdForHeyApi(flightDetails);


          if (!mapFlightIdToFlightDetails[flightId]) {
            mapFlightIdToFlightDetails[flightId] = [];
          }
          mapFlightIdToFlightDetails[flightId].push(flightDetails);
        }
      }
    }

    const statusDataList = await getFlightStatus(Object.keys(mapFlightIdToFlightDetails));
    for (let statusData of statusDataList) {
      let flightId = statusData.id;
      let flightDetailsList = mapFlightIdToFlightDetails[flightId];
      if (!flightDetailsList) {
        continue;
      }
      for (let flightDetails of flightDetailsList) {
        flightDetails.flightStatus = statusData;
        if (flightDetails.flightStatus?.latestStatus?.departureTerminal) {
          flightDetails.computed.departureTerminal = flightDetails.flightStatus?.latestStatus?.departureTerminal
        }
        if (flightDetails.flightStatus?.latestStatus?.departureGate) {
          flightDetails.computed.departureGate = flightDetails.flightStatus?.latestStatus?.departureGate
        }
        if (flightDetails.flightStatus?.latestStatus?.arrivalTerminal) {
          flightDetails.computed.arrivalTerminal = flightDetails.flightStatus?.latestStatus?.arrivalTerminal
        }
        if (flightDetails.flightStatus?.latestStatus?.arrivalGate) {
          flightDetails.computed.arrivalGate = flightDetails.flightStatus?.latestStatus?.arrivalGate
        }
        if (flightDetails.flightStatus?.latestStatus?.departureCheckInDesk) {
          flightDetails.computed.departureCheckInDesk = flightDetails.flightStatus?.latestStatus?.departureCheckInDesk
        }
        if (flightDetails.flightStatus?.latestStatus?.arrivalBaggageClaim) {
          flightDetails.computed.arrivalBaggageClaim = flightDetails.flightStatus?.latestStatus?.arrivalBaggageClaim
        }
      }
    }
  }

  async function storeTripsToFlutter(retrieved_trips: Trip[]) {
    if (!FlutterUtils.isFlutterWebapp()) {
      console.debug("[storeTripsToFlutter] Not a Flutter app");
      return;
    }
    if (retrieved_trips.length == 0) {
      console.debug("[storeTripsToFlutter] No trips to store to Flutter");
      return;
    }
    let lastTripPush_localStorageId = "lastTripPushToFlutter_"+HeyUtils.getInstance().getUserId()+"_"+retrieved_trips.length;
    let lastTripPushToFlutter = localStorage.getItem(lastTripPush_localStorageId);
    console.debug("[storeTripsToFlutter] Last trip push to Flutter:", lastTripPushToFlutter);
    // Todo: base this cache on the trip envelope to skip unnecessary pushes
    if (lastTripPushToFlutter && parseInt(lastTripPushToFlutter) > Date.now() - 1000 * 60 * 3) {
      console.debug("[storeTripsToFlutter] Not pushing offline trips to Flutter, last push was too recent");
      return;
    }

    await new Promise(resolve => setTimeout(resolve, 1000));
    if (allTripsHaveDestinationImage(retrieved_trips)) {
      console.debug("[storeTripsToFlutter] Trips images are ready, pushing to Flutter");
      sendTripsToFlutter(retrieved_trips, lastTripPush_localStorageId);
    } else {
      console.debug("[storeTripsToFlutter] Trip images are not ready yet, still push to Flutter to make sure we store trips");
      sendTripsToFlutter(retrieved_trips, lastTripPush_localStorageId);
      
      // Wait a bit for trips to have their destination image
      let nb_sec_to_wait = 10;
      while (nb_sec_to_wait > 0) {
        if (allTripsHaveDestinationImage(retrieved_trips)) {
          console.debug("[storeTripsToFlutter] All trip images are now ready! Updating trips to flutter.");
          return;
        }
        await new Promise(resolve => setTimeout(resolve, 1000));
        nb_sec_to_wait--;
      }
      sendTripsToFlutter(retrieved_trips, lastTripPush_localStorageId);
    }
  }

  function allTripsHaveDestinationImage(trips: Trip[]) {
    for (let trip of trips) {
      if (trip.isPast)
      {
        continue;
      }
      let image = getImageForTripId(trip.id)
      if (image) {
        trip.destinationImage = image;
      } else {
        return false;
      }
    }
    return true;
  }

  function sendTripsToFlutter(retrieved_trips: Trip[], localStrorageId: string) {
    // Send trips to flutter
    try {
      console.info("[storeTripsToFlutter] Pushing offline trips to Flutter");
      let tripRefsToKeep = []
      for (let trip of retrieved_trips) {
        if (trip.isPast) {
          continue;
        }
        tripRefsToKeep.push(trip.tripUid);
        FlutterUtils.storeTripForOffline(trip.tripUid, trip);
      }
      FlutterUtils.clearOfflineTrips(tripRefsToKeep);
      localStorage.setItem(localStrorageId, Date.now().toString());
    } catch (error) {
      localStorage.removeItem(localStrorageId);
      console.error('[storeTripsToFlutter] Error pushing offline trips to Flutter:', error);
    }
  }


  const { 
    data: trips = [], 
    isLoading,
    refetch: handleRefreshTrips,
  } = useQuery({
    queryKey: ['trips'],
    refetchOnWindowFocus: true,
    refetchInterval: 1000 * 60 * 15,
    refetchIntervalInBackground: true,
    refetchOnReconnect: true,
    refetchOnMount: true,
    queryFn: async () => {
      try {
        await Hey.HeyUtils.waitInit();
        await importAdtdTripIfNeeded();
        const tripshey = await Hey.HeyUtils.getInstance().getUserTrips();
        if (tripshey === null) {
          // toast({
          //   variant: "destructive",
          //   title: t("common.error"),
          //   description: t("index.errorFetchingTrips", "Please try again later ...")
          // });
          return [];
        }
        let trips = tripshey.map(buildTripFromHeyTrip);
        await retrieveFlightStatuses(trips);
        // Store trips to Flutter for offline access asynchronously (do not wait for it)
        storeTripsToFlutter(trips);
        return trips
        
      } catch (error) {
        console.error('Error fetching trips:', error);
        toast({
          variant: "destructive",
          title: t("common.error"),
          description: t("index.errorFetchingTrips", "Please try again later ...")
        });
        return [];
      }
    }
  });

  // Listen for notification count updates from Flutter
  useEffect(() => {
    const storedCount = localStorage.getItem('unreadNotificationCount');
    if (storedCount) {
      setNotificationCount(parseInt(storedCount, 10));
    }

    const handleNotificationCountUpdate = (event: CustomEvent<{ count: number }>) => {
      console.debug('Notification count updated:', event.detail.count);
      setNotificationCount(event.detail.count);
    };
    window.addEventListener('notificationCountUpdated', handleNotificationCountUpdate as EventListener);
    if (FlutterUtils.isFlutterWebapp() && (window as any).Flutter) {
      try {
        (window as any).Flutter.postMessage(JSON.stringify({
          type: 'getNotificationCount',
          data: {}
        }));
      } catch (error) {
        console.error('Error requesting notification count from Flutter:', error);
      }
    }
    return () => {
      window.removeEventListener('notificationCountUpdated', handleNotificationCountUpdate as EventListener);
    };
  }, []);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        await Hey.HeyUtils.waitInit();
        const userProfile = await Hey.HeyUtils.getInstance().getUserProfile();
        if (!userProfile) {
          console.error('User profile not found');
          const hasToken = localStorage.getItem(config.LS_KEY_TECH_TOKEN);
          if (hasToken) {
            console.debug('Token exists, Retrying to get profile and if it fails, clear tokens');
            // We wait a bit and retry
            await new Promise(resolve => setTimeout(resolve, 500));
            const userProfile = await Hey.HeyUtils.getInstance().getUserProfile();
            if (!userProfile) {
              console.error('User profile not found after retry');
              await Hey.HeyUtils.getInstance().signOut();
            }
          }
          if (!userProfile) {
            navigate("/auth");
            return;
          }
        }
        
        setFirstName(userProfile?.user?.firstName || null);
        if (userProfile?.user?.firstName) {
          FlutterUtils.setTitle(t("index.greetingWithName", "Hi, {{name}}", { name: userProfile?.user?.firstName }));
          localStorage.setItem("userName", userProfile?.user?.firstName);
        }
        
        if (userProfile?.user?.language && i18n.language !== userProfile.user.language) {
          console.debug(`🌐 Applying user language preference: ${userProfile.user.language}`);
          i18n.changeLanguage(userProfile.user.language);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
        const hasToken = localStorage.getItem(config.LS_KEY_TECH_TOKEN);
        
        navigate("/auth");
      }
    };
    
    const storedName = localStorage.getItem("userName");
    if (storedName) {
      setFirstName(storedName);
      FlutterUtils.setTitle(t("index.greetingWithName", "Hi, {{name}}", { name: storedName }));
    }
    else {
      FlutterUtils.setTitle(t("index.greeting"));
    }
    fetchUserProfile();
  }, [navigate, i18n]);

  const handleSignOut = async () => {
    trackHeaderAction("sign_out");
    try {
      if (await Hey.HeyUtils.getInstance().signOut()) {
        toast({
          title: t("auth.signOutSuccess", "Signed out successfully"),
          description: t("auth.redirectingHome", "Redirecting to home page...")
        });
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: t("common.error"),
        description: error.message
      });
    }
  };

  const handleOpenUpcomingFlights = () => {
    trackHeaderAction("upcoming_flights");
    setShowUpcomingFlights(true);
    setNotificationCount(0);
    localStorage.setItem('unreadNotificationCount', '0');
    if (FlutterUtils.isFlutterWebapp() && (window as any).Flutter) {
      try {
        (window as any).Flutter.postMessage(JSON.stringify({
          type: "clearNotificationCount"
        }));
      } catch (error) {
        console.error('Error sending clear notification count event to Flutter:', error);
      }
    }
  };

  useEffect(() => {
    if (location.hash == "#notifications") {
      location.hash = "";
      handleOpenUpcomingFlights();
    }
    else if (location.hash == "#logout") {
      location.hash = "";
      handleSignOut();
      navigate("/auth");
      FlutterUtils.setTitle(t("auth.signIn"));
    }
    else if (location.hash.indexOf("#nav-") === 0) {
      let path = location.hash.replace("#nav-", "");
      location.hash = "";
      console.log("Moving to path", path);
      navigate(path);
    }
  }, [location.hash]);
  useFlutterBridge(null);

  return (
    <div className="h-screen overflow-hidden bg-gradient-to-b from-travel-50 to-white">
      <DesktopLayout>
        <div className="h-full">
          <div className={`h-full ${!isMobile ? "pr-4" : ""}`}>
            {!FlutterUtils.isFlutterWebapp() && <header className="sticky top-0 z-[60] bg-white/80 backdrop-blur-md px-4 py-2 shadow-sm" style={{paddingTop: isFlutterWebapp ? "35px" : ""}}>
                <div className="flex justify-between items-center max-w-screen-xl mx-auto">
                  <div className="relative z-[61]">
                  <h1 className="text-xl font-bold leading-tight">
                      {firstName
                        ? t("index.greetingWithName", "Hi, {{name}}", { name: firstName })
                        : t("index.greeting")}
                    </h1>
                  <p className="text-sm text-travel-500 leading-tight">
                      {t("index.manageTrips")}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 relative z-[70]">
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={handleOpenUpcomingFlights}
                      aria-label={t("index.viewUpcomingFlights", "View upcoming flights")}
                      className="relative"
                    >
                      <Bell className="w-4 h-4" />
                      {notificationCount > 0 && (
                        <span className="absolute -top-1 -right-1 flex items-center justify-center w-4 h-4 text-[10px] font-bold text-white bg-red-500 rounded-full">
                          {notificationCount > 99 ? '99+' : notificationCount}
                        </span>
                      )}
                    </Button>
                    {isMobile && (
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={handleSignOut}
                        aria-label={t("auth.signOut")}
                      >
                        <LogOut className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </header>
            }
            {isMobile ? (
              <div className="h-[calc(100vh-4rem)] overflow-y-auto pb-16">
                {isLoading ? (
                  <div className="flex items-center justify-center h-40">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-travel-500" />
                  </div>
                ) : (
                    <Timeline trips={trips} handleRefreshTrips={handleRefreshTrips} />
                )}
              </div>
            ) : (
              <div 
                className="h-[calc(100vh-4rem)] overflow-y-auto scrollbar-hide"
                style={{ 
                  WebkitOverflowScrolling: 'touch'
                }}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-travel-500" />
                  </div>
                ) : (
                  <Timeline trips={trips} handleRefreshTrips={handleRefreshTrips} />
                )}
              </div>
            )}
          </div>
        </div>
      </DesktopLayout>

      <UpcomingFlightsDialog
        open={showUpcomingFlights}
        onOpenChange={setShowUpcomingFlights}
      />
    </div>
  );
};

export default Index;